import { useCallback, useState, type SVGProps } from 'react';
import { PanelLeftClose, PanelLeftOpen, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import { useTaskActions } from '@/features/tasks/model/useTaskActions';
import type { TaskApiPayload } from '@/features/tasks/model/forms/useTaskForm';
import TaskFormModal from '@/features/tasks/task-form-modal/TaskCreateModal';
import { cn } from '@/shared/lib/cn';
import { MeetingSlotModal } from '@/widgets/meeting-slot';

import {
  useSidebarCollapsed,
  useSidebarCollapseStore,
} from './sidebarCollapse';
import { SidebarTooltip } from './SidebarTooltip';

type DesktopSidebarFooterProps = {
  userId: number | null;
  isCreatingTask: boolean;
  onCreateIntermediateTask: (
    comment: string,
    durationMinutes: number,
    userId: number
  ) => void;
  onControlClick: () => void;
};

function ArrowIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="22"
      height="21"
      viewBox="0 0 22 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M1.477 10.772C1.21254 10.5453 1.08031 10.432 1.03186 10.2971C0.989333 10.1787 0.989333 10.0492 1.03186 9.93084C1.08031 9.79597 1.21254 9.68263 1.477 9.45594L10.6536 1.59027C11.1089 1.20005 11.3365 1.00495 11.5292 1.00017C11.6967 0.996013 11.8567 1.06959 11.9625 1.19946C12.0843 1.34889 12.0843 1.64869 12.0843 2.24829V6.90148C14.3969 7.30606 16.5134 8.47787 18.0863 10.2373C19.8011 12.1555 20.7496 14.6379 20.751 17.2108V17.8738C19.6142 16.5044 18.1948 15.3969 16.5902 14.627C15.1754 13.9483 13.646 13.5463 12.0843 13.4404V17.9796C12.0843 18.5792 12.0843 18.879 11.9625 19.0285C11.8567 19.1583 11.6967 19.2319 11.5292 19.2278C11.3365 19.223 11.1089 19.0279 10.6536 18.6377L1.477 10.772Z"
        stroke="#575757"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlusIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M10 1V19M1 10H19"
        stroke="#575757"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CalendarIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="20"
      height="22"
      viewBox="0 0 20 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M19 9H1M14 1V5M6 1V5M5.8 21H14.2C15.8802 21 16.7202 21 17.362 20.673C17.9265 20.3854 18.3854 19.9265 18.673 19.362C19 18.7202 19 17.8802 19 16.2V7.8C19 6.11984 19 5.27976 18.673 4.63803C18.3854 4.07354 17.9265 3.6146 17.362 3.32698C16.7202 3 15.8802 3 14.2 3H5.8C4.11984 3 3.27976 3 2.63803 3.32698C2.07354 3.6146 1.6146 4.07354 1.32698 4.63803C1 5.27976 1 6.11984 1 7.8V16.2C1 17.8802 1 18.7202 1.32698 19.362C1.6146 19.9265 2.07354 20.3854 2.63803 20.673C3.27976 21 4.11984 21 5.8 21Z"
        stroke="#575757"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const SIDEBAR_BUTTON_CLASS =
  'flex h-[28px] items-center justify-center rounded-[22px] text-[#313846] shadow-[0_8px_22px_rgba(17,24,39,0.04)] transition-[filter] hover:brightness-95 disabled:opacity-50';
const SIDEBAR_BUTTON_STYLE = {
  background:
    'linear-gradient(0deg, rgba(87,87,87,0.03), rgba(87,87,87,0.03)), linear-gradient(16.2deg, #E8E9EB 11.29%, #F5F7F6 91.93%)',
};

/** Кнопка «Создать задачу» — тот же модал/логика, что у зелёного FAB на
 *  странице задач (TasksControlPanel), только оформлена под пилюлю сайдбара. */
function CreateTaskSidebarButton({ className }: { className: string }) {
  const { createTask, isLoading } = useTaskActions();
  const [open, setOpen] = useState(false);

  const handleSubmit = useCallback(
    async (taskData: TaskApiPayload) => {
      try {
        await createTask(taskData);
        toast.success('Задача создана');
        setOpen(false);
      } catch (error) {
        console.error(error);
        toast.error('Не удалось создать задачу');
      }
    },
    [createTask]
  );

  return (
    <>
      <SidebarTooltip label="Создать задачу">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={className}
          style={SIDEBAR_BUTTON_STYLE}
          aria-label="Создать задачу"
        >
          <PlusIcon width={14} height={14} aria-hidden="true" />
        </button>
      </SidebarTooltip>

      <TaskFormModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        mode="create"
      />
    </>
  );
}

/**
 * Свернуть сайдбар до иконок и развернуть обратно; выбор запоминается.
 * Квадрат в ряду с «Контролем», а не своей строкой: каждая строка подвала
 * отнимает высоту у меню, и оно мельчает (см. useNavFitScale).
 */
function SidebarCollapseToggle() {
  const collapsed = useSidebarCollapsed();
  const toggle = useSidebarCollapseStore((state) => state.toggle);
  const label = collapsed ? 'Развернуть меню' : 'Свернуть меню';

  return (
    <SidebarTooltip label={label} always>
      <button
        type="button"
        onClick={toggle}
        aria-label={label}
        aria-expanded={!collapsed}
        className={cn(
          'flex shrink-0 items-center justify-center rounded-[12px] border border-[#E7EAF0] bg-white text-[#8A9099] transition-colors',
          'hover:bg-[#F6F7FA] hover:text-[#17244A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#102653]/20',
          collapsed
            ? 'mx-auto h-[28px] w-[44px]'
            : 'size-[clamp(38px,4.6vh,46px)]'
        )}
      >
        {collapsed ? (
          <PanelLeftOpen className="size-[18px]" strokeWidth={1.7} />
        ) : (
          <PanelLeftClose className="size-[18px]" strokeWidth={1.7} />
        )}
      </button>
    </SidebarTooltip>
  );
}

export function DesktopSidebarFooter({
  userId,
  isCreatingTask,
  onCreateIntermediateTask,
  onControlClick,
}: DesktopSidebarFooterProps) {
  const collapsed = useSidebarCollapsed();
  // Свёрнутый сайдбар: три кнопки столбиком — в ряд им там не хватит места.
  // Всё чуть ниже и плотнее: каждый пиксель подвала отнимается у иконок меню.
  const buttonClass = cn(
    SIDEBAR_BUTTON_CLASS,
    collapsed && 'h-[24px] w-[44px]'
  );

  return (
    <div
      className={cn(
        'flex shrink-0 flex-col pt-[clamp(2px,0.3vh,6px)]',
        collapsed ? 'gap-[8px]' : 'gap-[12.5px]'
      )}
    >
      <div
        className={
          collapsed
            ? 'flex flex-col items-center gap-[4px]'
            : 'grid grid-cols-3 gap-[clamp(4px,0.5vh,8px)]'
        }
      >
        <SidebarTooltip label="Промежутки">
          <button
            type="button"
            disabled={isCreatingTask || !userId}
            onClick={() =>
              userId &&
              onCreateIntermediateTask('Промежуточная задача', 20, userId)
            }
            className={buttonClass}
            style={SIDEBAR_BUTTON_STYLE}
            aria-label="Промежутки"
          >
            <ArrowIcon width={15} height={14} aria-hidden="true" />
          </button>
        </SidebarTooltip>

        <MeetingSlotModal>
          <SidebarTooltip label="Встречи">
            <button
              type="button"
              className={buttonClass}
              style={SIDEBAR_BUTTON_STYLE}
              aria-label="Встречи"
            >
              <CalendarIcon width={14} height={16} aria-hidden="true" />
            </button>
          </SidebarTooltip>
        </MeetingSlotModal>

        <CreateTaskSidebarButton className={buttonClass} />
      </div>

      {collapsed ? (
        <>
          <SidebarTooltip label="Контроль">
            <button
              type="button"
              onClick={onControlClick}
              aria-label="Контроль"
              className="mx-auto flex h-[32px] w-[44px] items-center justify-center rounded-[12px] border border-[#F0443E] bg-white text-[#F0443E] transition-colors hover:bg-[#FFF1F0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F0443E]/30"
            >
              <ShieldCheck className="size-[18px]" strokeWidth={1.8} />
            </button>
          </SidebarTooltip>
          <SidebarCollapseToggle />
        </>
      ) : (
        <div className="flex gap-[clamp(4px,0.5vh,8px)]">
          <button
            type="button"
            onClick={onControlClick}
            className="min-w-0 flex-1 rounded-[12px] border border-[#F0443E] bg-white text-center font-normal leading-none text-[#F0443E] transition-colors hover:bg-[#FFF1F0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F0443E]/30 h-[clamp(38px,4.6vh,46px)] text-[clamp(12px,1.4vh,14px)]"
          >
            Контроль
          </button>
          <SidebarCollapseToggle />
        </div>
      )}
    </div>
  );
}
